import * as Dfns from 'date-fns/fp';
import { z } from 'zod';
import {
  BpmDateString,
  IsoString,
  toBpmDateString
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  DatePrecision,
  EventType,
  SourceKind
} from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { CreateOracleEventBody } from '@blisspointmedia/bpm-types/dist/OracleApi';
import { App2Channels } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import {
  blankTo,
  optionalChoice,
  requiredChoice,
  requiredText
} from '@Components/Forms/form-field-utils';
import { DATE_FORMAT } from '@src/utils/data';
import { DerivedEventFields } from './oracle-taxonomy-utils';

export const createEventSchema = z.object({
  cid: requiredChoice<string>('Choose a client'),
  title: requiredText('Title is required'),
  eventType: requiredChoice<EventType>('Choose an event type'),
  date: requiredChoice<BpmDateString>('Pick a date'),
  // `HH:mm` from a time input, or '' for a day-precision event.
  time: z.string(),
  details: blankTo(undefined),
  recommendedAction: blankTo(undefined),
  source: optionalChoice<SourceKind>(),
  channel: optionalChoice<App2Channels>()
});

export type CreateEventFormValues = z.input<typeof createEventSchema>;
export type CreateEventSubmission = z.output<typeof createEventSchema>;

export const emptyCreateEventValues = (cid: string | null): CreateEventFormValues => ({
  cid,
  title: '',
  eventType: null,
  date: toBpmDateString(Dfns.format(DATE_FORMAT, new Date())),
  time: '',
  details: '',
  recommendedAction: '',
  source: null,
  channel: null
});

export const toCreateEventBody = (
  { time, date, ...fields }: CreateEventSubmission,
  { signalTag, weight, importance }: DerivedEventFields
): CreateOracleEventBody => {
  const commonFields = {
    ...fields,
    // Required by the create body, but themes are derived from eventType and never sent.
    categoryTags: [],
    signalTag,
    weight,
    importance
  };
  if (!time) {
    return { ...commonFields, datePrecision: DatePrecision.DAY, date };
  }
  return {
    ...commonFields,
    datePrecision: DatePrecision.TIME,
    date: new Date(`${date}T${time}:00`).toISOString() as IsoString
  };
};
